/**
 * Google Analytics 4 - Lightweight tracking utility
 *
 * Uses native gtag.js. All calls are no-ops when GA_MEASUREMENT_ID is empty
 * (i.e. local development without the env var set).
 *
 * Set VITE_GA_MEASUREMENT_ID in your .env to enable tracking.
 */

import { trackFunnelEvent } from './funnel';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || '';

function isEnabled(): boolean {
  return GA_MEASUREMENT_ID.length > 0 && typeof window !== 'undefined';
}

/**
 * Inject the gtag.js script and initialise GA4.
 * Safe to call multiple times; subsequent calls are ignored.
 *
 * Consent Mode v2 defaults are set in index.html (denied until user
 * accepts cookie banner). This function only loads gtag.js + configures
 * GA4 — it does NOT touch consent state. Call grantAnalyticsConsent()
 * from the cookie banner to flip consent to granted.
 *
 * The dataLayer + gtag stub already exist (set in index.html before this
 * runs), so we don't re-create them — that would clobber the consent
 * defaults already pushed.
 */
export function initGA(): void {
  if (!isEnabled()) return;
  if (document.getElementById('ga-gtag')) return; // already injected

  // 1. Async script loader for GA4 (Google Ads loader is in index.html)
  const script = document.createElement('script');
  script.id = 'ga-gtag';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // 2. Defensive guard: if Consent Mode block in index.html was removed,
  //    ensure dataLayer + gtag still exist so we don't crash.
  if (typeof window.gtag !== 'function') {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };
  }

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // we send page views manually on route change
  });
}

/**
 * GDPR: grant analytics + ads consent. Call from CookieConsent.accept().
 * Idempotent — safe to call on every page load to restore prior session
 * consent (read from localStorage).
 */
export function grantAnalyticsConsent(): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    'analytics_storage': 'granted',
    'ad_storage': 'granted',
    'ad_user_data': 'granted',
    'ad_personalization': 'granted',
  });
}

/**
 * GDPR: explicitly deny consent. Call from CookieConsent.decline().
 * Default state is already denied (set in index.html), so this is mostly
 * defensive — useful if a user changes mind after previously granting.
 */
export function denyAnalyticsConsent(): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    'analytics_storage': 'denied',
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
  });
}

/**
 * Track a virtual page view (SPA route change).
 */
export function trackPageView(page: string): void {
  if (!isEnabled()) return;
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: page,
  });
}

/**
 * Track a custom event.
 *
 * Dual-write: GA4 (gated on VITE_GA_MEASUREMENT_ID + consent via Consent Mode v2)
 * AND Supabase funnel_events (gated on auth or consent — see funnel.ts).
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>,
): void {
  if (isEnabled()) {
    window.gtag('event', eventName, params);
  }
  trackFunnelEvent(eventName, params);
}

/**
 * Google Ads offline-conversion sync was dropped for the TodAidesign
 * white-label (no Google Ads account). Kept as a no-op so existing call sites
 * (e.g. CheckoutSuccess) compile unchanged. To re-enable, add a TodAidesign
 * Ads ID (e.g. via a VITE_GOOGLE_ADS_ID env var) and load gtag for it.
 */
export function trackAdConversion(_transactionId: string, _value = 0): void {
  /* no-op: Google Ads disabled for TodAidesign */
}

// ---------------------------------------------------------------------------
// Predefined conversion events
// ---------------------------------------------------------------------------

export const ConversionEvents = {
  // Auth funnel
  SIGNUP_START: 'signup_start',
  SIGNUP_COMPLETE: 'signup_complete',
  LOGIN_COMPLETE: 'login_complete',
  EMAIL_VERIFIED: 'email_verified',

  // Activation
  DESIGNER_OPENED: 'designer_opened',
  ADDRESS_SEARCHED: 'address_searched',
  FIRST_CAMERA_PLACED: 'first_camera_placed',
  FIRST_PROJECT_CREATED: 'first_project_created',
  // Distinguishes new project from returning-user opens — without this,
  // designer_opened conflates first-time and Nth-time visits.
  PROJECT_LOADED: 'project_loaded',
  // Distinguishes "user manually deleted right after save" from
  // "save failed silently" — both leave first_project_created event with
  // no project in DB, but they're very different signals.
  PROJECT_DELETED: 'project_deleted',
  // Activation diagnostic — fires the moment the user confirms a save,
  // BEFORE the network round-trip. Pairs with PROJECT_SAVE_FAILED and
  // FIRST_PROJECT_CREATED so the 74 % drop from first_camera_placed →
  // first_project_created can be split into "didn't try" vs "tried + failed".
  PROJECT_SAVE_ATTEMPTED: 'project_save_attempted',
  // Reliability — distinguishes "user didn't try" from "user tried and got
  // an error". Without this, all designer-opens that don't end in a saved
  // project look like passive abandonment.
  PROJECT_SAVE_FAILED: 'project_save_failed',
  // Engagement reliability — heuristic that the user opened the designer
  // and went idle for ≥60s without producing meaningful state. Helps
  // distinguish "exploring then bouncing" from "actively working".
  DESIGNER_IDLE_60S: 'designer_idle_60s',

  // Value moments — fire when the user gets the thing they came for.
  // PDF export is the #1 value moment of the whole product.
  PDF_EXPORTED: 'pdf_exported',
  PDF_EXPORT_FAILED: 'pdf_export_failed',
  // Generic feature-use signal: pass `feature` property with one of
  // 'bom', 'validator', 'smart_quote', 'client_portal', 'dxf_export',
  // 'survey_mode', 'multi_floor', 'coverage_heatmap', etc. Lets us measure
  // adoption per Premium feature, not just paywall hits.
  FEATURE_USED: 'feature_used',
  // Survey-specific lifecycle events tied to the storage/upload fix shipped
  // 2026-05-07. Lets us verify uploads succeed in production after the fix.
  SURVEY_PHOTO_UPLOADED: 'survey_photo_uploaded',

  // Monetisation
  FEATURE_LIMIT_HIT: 'feature_limit_hit',
  UPGRADE_MODAL_SHOWN: 'upgrade_modal_shown',
  UPGRADE_MODAL_CLICKED: 'upgrade_modal_clicked',
  // Top-of-paid-funnel — distinguishes paywall hits from organic /pricing
  // visits. Without this, we can't tell if /pricing landing page is a
  // significant traffic source vs purely a click-through from upgrade modal.
  PRICING_PAGE_VIEWED: 'pricing_page_viewed',
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_COMPLETED: 'checkout_completed',
  // Fires when the Stripe redirect path can't even be entered — server
  // returned an error, no URL was returned, auth was missing, etc. Properties
  // carry `reason` (auth | api_error | already_subscribed | no_url | unknown)
  // + `plan` + `period` so the funnel can split "checkout drop 62%" into
  // "couldn't start checkout" vs "abandoned on Stripe page".
  CHECKOUT_FAILED: 'checkout_failed',
  // Bottom-of-funnel churn signal — fires from webhook-stripe when Stripe
  // emits customer.subscription.deleted. Captures the moment paid users
  // leave, which we previously had no visibility into.
  SUBSCRIPTION_CANCELED: 'subscription_canceled',
  PLAN_SELECTED: 'plan_selected',

  // Engagement
  CTA_CLICKED: 'cta_clicked',

  // ── Phase 2 instrumentation (added 2026-05-11) ──────────────────────

  // Onboarding flow (4 steps: language → use case → units → tutorial).
  // Without these we couldn't tell where users abandon the welcome wizard.
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_SKIPPED: 'onboarding_skipped',
  ONBOARDING_COMPLETED: 'onboarding_completed',

  // Auth friction — every supabase.auth.* error path. Pairs with
  // signup_complete (server trigger) to show "tried but failed" populations.
  AUTH_FAILED: 'auth_failed',

  // Every successful project save (first_project_created only fires once
  // per device × user, so power-user saves were invisible).
  PROJECT_SAVED: 'project_saved',

  // Tool selection in the designer toolbar. Tells us which drawing tools
  // (camera, fence, building, gate, equipment, cable, measurement, note)
  // actually get used — beyond just first_camera_placed.
  TOOL_SELECTED: 'tool_selected',

  // Multi-floor depth-of-use. floor_added fires on every add, plan-gated.
  FLOOR_ADDED: 'floor_added',
  FLOOR_PLAN_UPLOADED: 'floor_plan_uploaded',

  // Disambiguate "typed an address" from "got a valid hit". Without this,
  // address_searched events hide the fact that geocoding failed.
  ADDRESS_GEOCODED_RESULT: 'address_geocoded_result',

  // Marketing attribution. PRICING_PAGE_VIEWED already exists; this adds
  // generic page-level views with deduplication by path-per-session.
  LANDING_VIEWED: 'landing_viewed',
  PAGE_VIEWED: 'page_viewed',
  // One-time-per-session classification of how the user arrived.
  REFERRER_CLASSIFIED: 'referrer_classified',

  // Pricing UX: monthly vs yearly toggle. 96 plan_selected → 29
  // checkout_started suggests period switch is a friction point.
  PRICING_TABLE_TOGGLED: 'pricing_table_toggled',
  // Distinct from "didn't click upgrade" — fires when modal closes without
  // an upgrade. With upgrade_modal_shown we'll have full open→outcome view.
  UPGRADE_MODAL_DISMISSED: 'upgrade_modal_dismissed',
  // Stripe webhook fires this on invoice.payment_succeeded for recurring
  // billing. Pairs with subscription_canceled for paid retention curves.
  SUBSCRIPTION_RENEWED: 'subscription_renewed',

  // Session lifecycle. session_ended fires on visibilitychange→hidden with
  // duration_ms property. returning_session fires once per session for
  // users whose signup_complete is >7 days old.
  SESSION_ENDED: 'session_ended',
  RETURNING_SESSION: 'returning_session',

  // Device-groups feature (visibility layer for client before/after
  // presentations). Tracks whether the feature is actually used and how —
  // critical for deciding whether to invest in v2 bulk-assign / auto-
  // grouping beyond by-manufacturer.
  GROUP_CREATED: 'group_created',
  GROUP_DELETED: 'group_deleted',
  GROUP_VISIBILITY_TOGGLED: 'group_visibility_toggled',
  GROUP_ENTITY_ASSIGNED: 'group_entity_assigned',
  GROUP_AUTO_BY_MANUFACTURER: 'group_auto_by_manufacturer',

  // ── /try anonymous sandbox (2026-06-22) ─────────────────────────────
  // The camera-page CTA now drops anon visitors onto an EDITABLE sandbox
  // (vs the old read-only /demo). Measures the anon-visitor → signup leak.
  // cta_bucket (try|demo) rides on the relevant events for the A/B.
  SANDBOX_OPENED: 'sandbox_opened',
  SANDBOX_CAMERA_PLACED: 'sandbox_camera_placed',
  SANDBOX_GATED_ACTION_HIT: 'sandbox_gated_action_hit',
  SANDBOX_TO_SIGNUP: 'sandbox_to_signup',
  FIRST_PROJECT_FROM_SANDBOX: 'first_project_from_sandbox',
} as const;
